import ElementLoader from "components/ElementLoader";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { nameColumn } from "containers/Resources/screens/ResourcesSingle/components/UsedByList/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FieldInputProps, FieldMetaState } from "react-final-form";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getNewRecordPath } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import { buildCreatePrefillParams } from "containers/Resources/screens/ResourcesSingle/createPrefill";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";
import RecordLabel from "components/RecordLabel";
import { ReferenceGroup, useReferencesTo } from "utils/useReferencesTo";
import { useSchemaFormRecord } from "components/Form/components/SchemaForm/SchemaFormRecordContext";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { graphqlPost } from "api/graphqlClient";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { Button } from "components/Button/Button";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { buildReferenceValueOptions } from "containers/Filter/FilterBuilder/utils";
import styles from "containers/Resources/screens/ResourcesSingle/components/UsedByList/UsedByList.module.scss";
import { getSchemaFormColumns } from "containers/Resources/screens/ResourcesSingle/AttributeValueSchema";

/**
 * Inline `form-incoming-reference` schema-form element.
 *
 * Renders the records that reference the CURRENT record through ONE configured
 * incoming relation (Stefan's Option A: an element is scoped to a single
 * referencing object_template + the attribute on it that points back here). It
 * is the inline, in-place counterpart of the record-level "Used by" tab, driven
 * by the same `POST /references-to` endpoint via the shared {@link useReferencesTo}
 * hook, but narrowed to the one relation the attribute declares.
 *
 * When the backend emits `read_only: false` (the relation is fully configured —
 * both the referencing object_template and the back-reference attribute are set)
 * each row gains inline hover actions to REMOVE the referrer: "Unlink" (null the
 * back-reference FK on the referrer, keeping the record) or "Delete record"
 * (delete the referrer outright). Add-row (create / link a referrer with the
 * back-reference pre-filled) is the remaining Phase-3 slice.
 */

export interface IncomingReferenceNode {
    /** object_template id of the referencing records to show. */
    referencing_object_template_id?: string;
    /** Name of the OBJECT_TEMPLATE_REFERENCE attribute on them pointing back here. */
    incoming_reference_attribute?: string;
    /** Optional label-derivation config (unused in Phase 2 — label defaults to name). */
    label_template?: unknown;
    /** Whether the element is read-only (always true in Phase 2). */
    read_only?: boolean;
}

export interface IncomingReferenceRow {
    id: string;
    name?: string;
    record_type: string;
    /** object_template id of the referencing record (for the Template column). */
    object_template_id?: string;
    /** Projected attribute values of the referencing custom object (attribute columns). */
    value?: Record<string, unknown>;
}

/** Maximum number of the referencing template's attributes shown as columns. */
export const MAX_INCOMING_REFERENCE_ATTRIBUTE_COLUMNS = 4;

/**
 * Pure selector: which of the referencing template's attributes to show as
 * columns in the inline table (Stefan 2026-10-13: "add columns of the other type
 * attributes"). Takes the template's schema-form `schema` (properties in
 * attribute order + `hidden` list) and returns the first `max` attribute keys
 * that are not hidden, skipping the back-reference attribute itself — on every
 * row it points at the record being viewed, so a column for it would repeat the
 * same value. The cap keeps the inline mini-table compact inside a record form.
 */
export const selectIncomingReferenceAttributeKeys = (
    schema: { properties?: Record<string, unknown>; hidden?: string[] } | null | undefined,
    backReferenceAttribute: string | undefined,
    max: number = MAX_INCOMING_REFERENCE_ATTRIBUTE_COLUMNS
): string[] => {
    if (!schema?.properties) {
        return [];
    }
    const hidden = new Set(schema.hidden ?? []);
    return Object.keys(schema.properties)
        .filter((key) => key !== backReferenceAttribute && !hidden.has(key))
        .slice(0, max);
};

/**
 * Pure helper: attach the fetched attribute values to the rows (by id). Rows
 * whose value has not been fetched yet keep `value` undefined and render empty
 * attribute cells until the fetch lands.
 */
export const mergeIncomingReferenceValues = (
    rows: IncomingReferenceRow[],
    values: Record<string, Record<string, unknown>>
): IncomingReferenceRow[] =>
    rows.map((row) => (row.id in values ? { ...row, value: values[row.id] } : row));

/**
 * Pure selector: flatten the `/references-to` groups down to the rows for ONE
 * configured relation. Extracted from the component so the matching logic is
 * unit-testable without React / RecordTable.
 *
 * A group matches when it is the referencing object_template we were configured
 * with AND it is reached via the configured attribute. The backend exposes the
 * attribute name under `via_attribute` for custom-object reference attributes
 * (and `via_column` for a plain FK column); both are checked so the selector is
 * robust to which the backend emits. When no attribute is configured yet (older
 * node, or mid-edit), relation identity falls back to the object_template match
 * alone.
 */
export const selectIncomingReferenceRows = (
    groups: ReferenceGroup[] | null | undefined,
    node: Pick<IncomingReferenceNode, "referencing_object_template_id" | "incoming_reference_attribute">
): { rows: IncomingReferenceRow[]; truncated: boolean } => {
    const all = groups ?? [];
    const matching = all.filter((group) => {
        if (
            node.referencing_object_template_id &&
            group.referencing_object_template_id !== node.referencing_object_template_id
        ) {
            return false;
        }
        if (node.incoming_reference_attribute) {
            const via = group.via_attribute ?? group.via_column;
            if (via !== node.incoming_reference_attribute) {
                return false;
            }
        }
        return true;
    });
    const rows: IncomingReferenceRow[] = matching.flatMap((group) => {
        const feType = getTypedLowerCase(group.referencing_record_type);
        return group.items.map((item) => ({
            id: item.id,
            name: item.name ?? undefined,
            record_type: feType,
            object_template_id: group.referencing_object_template_id,
        }));
    });
    return { rows, truncated: matching.some((group) => group.truncated) };
};

/**
 * Pure predicate: may the element offer in-place remove actions?
 *
 * The backend emits `read_only: false` only when the relation is FULLY
 * configured (both the referencing object_template id and the back-reference
 * attribute name are present) — the two pieces the remove actions need to null
 * the FK or identify the referrer. A missing/true `read_only`, or a missing
 * attribute, keeps the element read-only so the frontend never offers an action
 * it cannot complete. Extracted so the gating is unit-testable without React.
 */
export const isIncomingReferenceEditable = (
    node: Pick<IncomingReferenceNode, "read_only" | "incoming_reference_attribute">
): boolean => node.read_only === false && Boolean(node.incoming_reference_attribute);

/**
 * Pure predicate: may the element offer the per-row "Unlink" action?
 *
 * Unlink nulls the back-reference FK on the referrer. That is only valid when the
 * back-reference attribute is NULLABLE — on a NOT-NULL back-reference the null
 * write fails with a constraint violation (the error Stefan hit, 2026-10-13), so
 * the action must not be offered. `backRefNullable === null` means "unknown"
 * (metadata not yet loaded, or not resolvable) and is treated as non-nullable so
 * the frontend never offers an action it cannot complete. Delete (removing the
 * whole referrer record) is unaffected and stays available whenever editable.
 */
export const canUnlinkIncomingReference = (
    editable: boolean,
    backRefNullable: boolean | null
): boolean => editable && backRefNullable === true;

/**
 * Pure predicate: may the element offer the "Link existing record" add-row?
 *
 * Linking points an existing referrer's back-reference FK at this record. It is
 * only meaningful when the back-reference is NULLABLE: a NOT-NULL back-reference
 * is already set on every referrer, so "linking an existing record" could only
 * ever silently re-parent (steal) a record away from its current owner. So the
 * add-row is offered only for a nullable back-reference (Stefan 2026-10-13).
 * `backRefNullable === null` (unknown) is treated as non-nullable → hidden.
 */
export const canLinkExistingIncomingReference = (
    editable: boolean,
    backRefNullable: boolean | null
): boolean => editable && backRefNullable === true;

/**
 * Pure helper: hide rows that were just unlinked/deleted but may still be
 * reported by a racing `/references-to` refetch.
 *
 * A custom-object write (delete, or the unlink FK-null) is not guaranteed to be
 * visible to the very next `/references-to` read, so `refetch()` alone can leave
 * a just-removed row lingering for a beat — janky to a real user. We therefore
 * remove the row from the displayed set OPTIMISTICALLY on a successful mutation
 * and reconcile against the authoritative fetch via {@link reconcileRemovedIds}.
 */
export const applyOptimisticRemovals = (
    rows: IncomingReferenceRow[],
    removedIds: readonly string[]
): IncomingReferenceRow[] =>
    removedIds.length === 0 ? rows : rows.filter((row) => !removedIds.includes(row.id));

/**
 * Pure helper: prune the optimistic-removal set down to the ids the backend
 * STILL reports as present.
 *
 * Keeps the set bounded and self-healing: once a fetch confirms a removed id is
 * gone it stops being suppressed (so a later re-link of the same record
 * reappears), while ids whose removal has not yet propagated stay suppressed.
 * Returns the same array instance when nothing changed so callers can skip a
 * redundant state update.
 */
export const reconcileRemovedIds = (
    removedIds: readonly string[],
    presentIds: ReadonlySet<string>
): string[] => {
    const stillPresent = removedIds.filter((id) => presentIds.has(id));
    return stillPresent as string[];
};

/**
 * Pure helper: build the new `value` blob for a referrer whose back-reference FK
 * is being set (link-existing → the current record's id) or cleared (unlink →
 * null). custom_object.value is REPLACED wholesale by the GraphQL update
 * mutation, so the caller re-sends the whole value with only the one FK key
 * changed. Extracted so the merge semantics are unit-testable without React.
 */
export const buildReferrerFkPatch = (
    currentValue: Record<string, unknown> | null | undefined,
    attribute: string,
    fkValue: string | null
): Record<string, unknown> => ({ ...(currentValue ?? {}), [attribute]: fkValue });

/**
 * Pure helper: the add-row button label.
 *
 * Names the ONE referencing template the user can actually link ("Link
 * <Template>") rather than the generic "Link existing record". A referencing
 * record is always a custom object of one specific template (the template that
 * declares a reference back to this one), so naming it is both shorter and more
 * accurate: it drops the redundant "existing" (a non-existent record cannot be
 * linked) and the misleading "record" (you link custom objects of a particular
 * template, not arbitrary records). Falls back to a bare "Link" until the
 * template name resolves. (Margot 2026-10-21.)
 */
export const incomingReferenceLinkLabel = (templateName?: string | null): string =>
    templateName ? `Link ${templateName}` : "Link";

/**
 * Pure helper: the "create new referrer" button label ("Create <Template>"),
 * the create-new counterpart of {@link incomingReferenceLinkLabel}. Falls back
 * to a bare "Create" until the template name resolves.
 */
export const incomingReferenceCreateLabel = (templateName?: string | null): string =>
    templateName ? `Create ${templateName}` : "Create";

/**
 * Pure predicate: may the element offer "Create <Template>"?
 *
 * Creating a new referrer sets its back-reference to this record, so — unlike
 * link-existing / unlink — it is valid for a NOT-NULL back-reference too. It
 * needs an editable element, an existing current record (its id is the
 * prefilled back-reference) and a configured referencing template.
 */
export const canCreateIncomingReference = (
    editable: boolean,
    recordId: string | null | undefined,
    referencingObjectTemplateId: string | null | undefined
): boolean => editable && Boolean(recordId) && Boolean(referencingObjectTemplateId);

/**
 * Pure helper: the create-form path for a new referrer of `templateId` with the
 * back-reference `attribute` prefilled to `recordId`. `stay` keeps the user on
 * the current record after saving (the element refetches when it remounts).
 */
export const buildIncomingReferenceCreatePath = (
    templateId: string,
    templateName: string | undefined,
    attribute: string,
    recordId: string
): string =>
    getNewRecordPath("custom_object", {
        object_template_id: templateId,
        ...(templateName ? { object_template_name: templateName } : {}),
        ...buildCreatePrefillParams({ [attribute]: recordId }, { stay: true }),
    });

const initialQuery = { limit: 25, order: "+name", offset: 0 };

/**
 * Resolve whether the configured back-reference attribute is nullable. The
 * element gates Unlink and Link-existing on this (see canUnlinkIncomingReference
 * / canLinkExistingIncomingReference) so it never offers an action that would hit
 * a NOT-NULL constraint. Scoped by the referencing template id + the attribute
 * name the node already carries (same object_template_attribute_list shape the
 * IncomingReferenceAttributeEditor uses).
 */
const BACK_REFERENCE_NULLABLE_QUERY = `query incomingReferenceBackRefNullable($filter: String) {
    object_template_attribute_list(filter: $filter, limit: 1, offset: 0) {
        object_template_attribute_list {
            id
            is_nullable
        }
    }
}`;

/**
 * Resolve the display name of each referencing object_template so the Template
 * column can label the rows (Margot 2026-10-20: "show the template of the
 * referencing record"). Scoped to the distinct template ids present in the
 * current rows; typically one id (the element is scoped to a single referencing
 * template), but the query takes a list so the unconfigured multi-template case
 * is covered too.
 */
const TEMPLATE_NAMES_QUERY = `query incomingReferenceTemplateNames($filter: String) {
    object_template_list(filter: $filter, limit: 100, offset: 0) {
        object_template_list {
            id
            name
        }
    }
}`;

const REFERENCING_TEMPLATE_SCHEMA_QUERY = `query incomingReferenceTemplateSchema($filter: String) {
    object_template_plain_list(filter: $filter) { id schema }
}`;

const REFERRER_VALUES_QUERY = `query incomingReferenceValues($filter: String, $limit: Int, $value_keys: [String!]) {
    custom_object_plain_list(filter: $filter, limit: $limit, value_keys: $value_keys) { id value }
}`;

const tableOptions = {
    toolbar: "controls" as const,
    toolbarFilters: false,
    showSearch: false,
    viewColumns: false,
    showDensityToggle: false,
    selectableRows: "none" as const,
    // Compact rows: this is an inline mini-table embedded in a record form, so
    // vertical compactness reads better than the full-page list default.
    density: "high" as const,
};

export interface IncomingReferenceProps {
    node: IncomingReferenceNode;
}

export const IncomingReference = ({ node }: IncomingReferenceProps) => {
    const { recordType, recordId } = useSchemaFormRecord();

    // No backing record yet (e.g. the create screen, before the record exists):
    // there can be no inbound references, so say so rather than firing a lookup
    // against a nonexistent id.
    const hasRecord = Boolean(recordType && recordId);

    const { groups, isLoading, error, refetch } = useReferencesTo(recordType ?? "", recordId ?? "");

    const { rows: fetchedRows, truncated } = useMemo(
        () => selectIncomingReferenceRows(groups, node),
        [groups, node]
    );

    // Rows a successful unlink/delete removed in-session, hidden immediately so
    // the table does not keep showing a just-removed row while the backend write
    // propagates to the next /references-to read (see applyOptimisticRemovals).
    const [removedIds, setRemovedIds] = useState<string[]>([]);

    const rows = useMemo(
        () => applyOptimisticRemovals(fetchedRows, removedIds),
        [fetchedRows, removedIds]
    );

    // Reconcile the optimistic set against each authoritative fetch: drop ids the
    // backend now agrees are gone (keeps the set bounded + lets a re-link of the
    // same record reappear). Converges — reconcileRemovedIds is idempotent once
    // pruned, so re-running on the resulting removedIds change is a no-op.
    useEffect(() => {
        if (removedIds.length === 0) {
            return;
        }
        const presentIds = new Set(fetchedRows.map((row) => row.id));
        const pruned = reconcileRemovedIds(removedIds, presentIds);
        if (pruned.length !== removedIds.length) {
            setRemovedIds(pruned);
        }
    }, [fetchedRows, removedIds]);

    const markRemoved = useCallback((id: string) => {
        setRemovedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    // Resolve referencing-template display names for the Template column. Keyed
    // by object_template id; looked up once per distinct id present in the rows.
    const [templateNames, setTemplateNames] = useState<Record<string, string>>({});
    const templateIdsKey = useMemo(() => {
        const ids = Array.from(
            new Set(
                [
                    ...rows.map((row) => row.object_template_id),
                    // Resolve the configured referencing template too, so the
                    // "Link <Template>" button can name it even in the empty
                    // state (no rows present yet).
                    node.referencing_object_template_id,
                ].filter(Boolean) as string[]
            )
        ).sort();
        return ids.join(",");
    }, [rows, node.referencing_object_template_id]);
    useEffect(() => {
        const ids = templateIdsKey ? templateIdsKey.split(",") : [];
        const missing = ids.filter((id) => !(id in templateNames));
        if (missing.length === 0) {
            return;
        }
        let cancelled = false;
        const filter = JSON.stringify({ field: "id", op: "in", value: missing });
        graphqlPost({ query: TEMPLATE_NAMES_QUERY, variables: { filter }, allow_normal: true })
            .then((response: any) => {
                if (cancelled) {
                    return;
                }
                const list: Array<{ id: string; name: string }> =
                    response?.data?.object_template_list?.object_template_list ?? [];
                if (list.length === 0) {
                    return;
                }
                setTemplateNames((prev) => {
                    const next = { ...prev };
                    list.forEach((tmpl) => {
                        if (tmpl?.id) {
                            next[tmpl.id] = tmpl.name;
                        }
                    });
                    return next;
                });
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [templateIdsKey, templateNames]);

    const dispatch = useDispatch();
    const history = useHistory();
    const attribute = node.incoming_reference_attribute;
    const editable = isIncomingReferenceEditable(node);

    // Whether the back-reference attribute on the referencing template is
    // nullable. Unlink (null the FK) and Link-existing (move an FK onto this
    // record) are only valid when the FK CAN be null — a NOT-NULL back-reference
    // cannot be unlinked (constraint violation) and "linking existing" could only
    // silently re-parent another record. So those two actions are offered only
    // for a nullable back-reference; Delete is unaffected. `null` = unknown
    // (loading / not resolvable) → treated as non-nullable so we never surface an
    // action that errors. (Stefan 2026-10-13.)
    const [backRefNullable, setBackRefNullable] = useState<boolean | null>(null);
    useEffect(() => {
        const otId = node.referencing_object_template_id;
        const attr = node.incoming_reference_attribute;
        if (!otId || !attr) {
            setBackRefNullable(null);
            return;
        }
        let cancelled = false;
        const filter = JSON.stringify({
            and: [
                { field: "object_template_id", op: "eq", value: otId },
                { field: "name", op: "eq", value: attr },
            ],
        });
        graphqlPost({
            query: BACK_REFERENCE_NULLABLE_QUERY,
            variables: { filter },
            allow_normal: true,
        })
            .then((response: any) => {
                if (cancelled) {
                    return;
                }
                const row =
                    response?.data?.object_template_attribute_list
                        ?.object_template_attribute_list?.[0];
                setBackRefNullable(row ? Boolean(row.is_nullable) : null);
            })
            .catch(() => {
                if (!cancelled) {
                    setBackRefNullable(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [node.referencing_object_template_id, node.incoming_reference_attribute]);

    // Attribute columns: the referencing template's schema decides which of its
    // attributes are shown (selectIncomingReferenceAttributeKeys); the rows'
    // values are then fetched with a value_keys projection so large attributes
    // that no column shows are never loaded.
    const [referencingSchema, setReferencingSchema] = useState<Record<string, any> | null>(null);
    useEffect(() => {
        const otId = node.referencing_object_template_id;
        if (!otId) {
            setReferencingSchema(null);
            return;
        }
        let cancelled = false;
        const filter = JSON.stringify({ field: "id", op: "eq", value: otId });
        graphqlPost({ query: REFERENCING_TEMPLATE_SCHEMA_QUERY, variables: { filter } })
            .then((response: any) => {
                if (!cancelled) {
                    setReferencingSchema(response?.data?.object_template_plain_list?.[0]?.schema ?? null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setReferencingSchema(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [node.referencing_object_template_id]);

    const attributeKeys = useMemo(
        () => selectIncomingReferenceAttributeKeys(referencingSchema, attribute),
        [referencingSchema, attribute]
    );
    const attributeKeysKey = attributeKeys.join(",");
    const rowIdsKey = useMemo(() => rows.map((row) => row.id).sort().join(","), [rows]);
    const [rowValues, setRowValues] = useState<Record<string, Record<string, unknown>>>({});
    useEffect(() => {
        const ids = rowIdsKey ? rowIdsKey.split(",") : [];
        const keys = attributeKeysKey ? attributeKeysKey.split(",") : [];
        if (ids.length === 0 || keys.length === 0) {
            return;
        }
        let cancelled = false;
        const filter = JSON.stringify({ field: "id", op: "in", value: ids });
        graphqlPost({
            query: REFERRER_VALUES_QUERY,
            variables: { filter, limit: ids.length, value_keys: keys },
        })
            .then((response: any) => {
                if (cancelled) {
                    return;
                }
                const list: Array<{ id: string; value: Record<string, unknown> | null }> =
                    response?.data?.custom_object_plain_list ?? [];
                const next: Record<string, Record<string, unknown>> = {};
                list.forEach((rec) => {
                    if (rec?.id) {
                        next[rec.id] = rec.value ?? {};
                    }
                });
                setRowValues(next);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [rowIdsKey, attributeKeysKey]);

    const displayRows = useMemo(() => mergeIncomingReferenceValues(rows, rowValues), [rows, rowValues]);

    const attributeColumns = useMemo(
        () =>
            referencingSchema && attributeKeys.length > 0
                ? getSchemaFormColumns(referencingSchema, { includeKeys: attributeKeys })
                : [],
        [referencingSchema, attributeKeys]
    );

    const canUnlink = canUnlinkIncomingReference(editable, backRefNullable);
    const canLinkExisting = canLinkExistingIncomingReference(editable, backRefNullable);

    // Display name of the ONE referencing template, for the "Link <Template>"
    // add-row button label (resolved alongside the Template column names).
    const referencingTemplateName = node.referencing_object_template_id
        ? templateNames[node.referencing_object_template_id]
        : undefined;

    // Whether the "link existing record" picker is open (add-row, link-existing
    // mode). A key bump force-remounts the react-select after a successful link
    // so it clears its selection for the next add.
    const [picking, setPicking] = useState(false);
    const [pickerKey, setPickerKey] = useState(0);

    // Set (or clear) the back-reference FK on a referrer record. custom_object
    // .value is REPLACED wholesale by the GraphQL update mutation, so we read the
    // referrer's current value + record_version and re-send the full value with
    // only the FK key changed (expected_version guards a concurrent edit).
    // Mirrors AttributeValueSchema.dispatchAttributeUpdate. Shared by unlink
    // (fkValue = null) and link-existing (fkValue = this record's id).
    const writeReferrerFk = useCallback(
        async (rowId: string, fkValue: string | null) => {
            if (!attribute) {
                throw new Error("no back-reference attribute configured");
            }
            const res = await graphqlPost({
                query: "query ($id: String!) { custom_object(id: $id) { id value record_version } }",
                variables: { id: rowId },
            });
            const rec = res?.data?.custom_object;
            if (!rec) {
                throw new Error("referencing record not found");
            }
            await new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_UPDATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "custom_object",
                    variables: {
                        id: rowId,
                        value: buildReferrerFkPatch(rec.value, attribute, fkValue),
                        expected_version: rec.record_version,
                    },
                });
            });
        },
        [attribute, dispatch]
    );

    // Unlink: null the back-reference FK on the referrer, keeping the record.
    const unlinkRow = useCallback(
        (row: IncomingReferenceRow) => {
            if (!attribute) {
                return;
            }
            showConfirmModal({
                title: `Unlink “${row.name ?? row.id}”?`,
                content:
                    "Removes the reference to this record. The referencing record itself is kept.",
                confirmText: "Unlink",
                cancelButtonProps,
                className: "confirmationModal",
                centered: true,
                onConfirm: async () => {
                    try {
                        await writeReferrerFk(row.id, null);
                        // Hide the row now (optimistic) + reconcile via refetch.
                        markRemoved(row.id);
                        refetch();
                    } catch {
                        // The update saga raises its own error notification; swallow
                        // here so the rejection is not reported as unhandled.
                    }
                },
            });
        },
        [attribute, writeReferrerFk, markRemoved, refetch]
    );

    // Link existing: point an already-existing referrer's back-reference FK at
    // THIS record, so it joins the incoming-reference set. The write is the
    // inverse of unlink (fkValue = this record's id) and reuses the same proven
    // path; only the picker UI (a resource-backed react-select scoped to the
    // configured referencing object_template) is new. A newly-linked row appears
    // on the next /references-to refetch.
    const linkExisting = useCallback(
        async (targetId: unknown) => {
            if (!attribute || typeof targetId !== "string" || !targetId || !recordId) {
                return;
            }
            try {
                await writeReferrerFk(targetId, recordId);
                // If this record was just unlinked/deleted and is being re-linked,
                // stop suppressing it so it reappears.
                setRemovedIds((prev) => prev.filter((id) => id !== targetId));
                setPicking(false);
                setPickerKey((k) => k + 1);
                refetch();
            } catch {
                // The update saga raises its own error notification.
            }
        },
        [attribute, writeReferrerFk, recordId, refetch]
    );

    // Template column: show the object_template of the referencing records so a
    // user can see what kind of records reference this one (Margot 2026-10-20).
    // Renders a RecordLabel linking to the template (consistent with the "Object
    // template" column on the Used-by tab); falls back to the record type while
    // the name resolves.
    const templateColumn = useMemo(
        () => ({
            label: "Template",
            key: "object_template",
            sortable: false,
            shown: true,
            dto: (row: IncomingReferenceRow) => {
                const otId = row.object_template_id;
                const name = otId ? templateNames[otId] : undefined;
                if (otId && name) {
                    return (
                        <RecordLabel
                            name={name}
                            link={getRelativeUrl("object_template", otId)}
                            iconByType="object_template"
                        />
                    );
                }
                return <span className={styles.referenceGroupVia}>{row.record_type}</span>;
            },
        }),
        [templateNames]
    );

    const columns = useMemo(() => {
        // Unlink is the only in-place action — Delete was removed (Margot
        // 2026-10-20: a destructive record delete does not belong in this table;
        // Unlink just breaks the reference). The actions column is therefore shown
        // only when Unlink is offered (a nullable back-reference); otherwise there
        // is no action, so no column.
        // Every row comes from the same referencing template (the element filters
        // on one referencing_object_template_id), so once attribute columns are
        // shown the Template column only repeats one value; it is dropped then to
        // keep the inline table within the form width (the template is still
        // named on the Create/Link buttons). Without attribute columns it stays.
        const leading =
            attributeColumns.length > 0
                ? [nameColumn, ...attributeColumns]
                : [nameColumn, templateColumn];
        if (!canUnlink) {
            return leading;
        }
        const actionsColumn = {
            // A real header over the always-visible action, aligned like other
            // action columns (e.g. MetadataList): left-aligned icon button, shown
            // at rest rather than hover-revealed (Margot 2026-10-20).
            label: "Actions",
            key: "actions",
            sortable: false,
            shown: true,
            dto: (row: IncomingReferenceRow) => (
                <div className="incomingRefRowActions">
                    <Tooltip overlay="Unlink">
                        <Button
                            icon={IconCollection.linkOff}
                            variation="icon"
                            size="small"
                            onClick={() => unlinkRow(row)}
                        />
                    </Tooltip>
                </div>
            ),
        };
        return [...leading, actionsColumn];
    }, [canUnlink, unlinkRow, templateColumn, attributeColumns]);

    if (!hasRecord) {
        return (
            <div className={styles.referencesEmpty}>
                References appear once this record is saved.
            </div>
        );
    }

    if (isLoading && groups === null) {
        return (
            <div className={styles.referencesLoading}>
                <ElementLoader />
            </div>
        );
    }

    if (error) {
        return <div className={styles.referencesEmpty}>{error}</div>;
    }

    // Add-row (link-existing): a quiet "Link <Template>" button that reveals a
    // resource-backed picker (react-select) scoped to the configured referencing
    // object_template; choosing a record points its back-reference FK at this
    // record. Follows ui-design-principle-buttons (small, dark). The label names
    // the referencing template and uses a link (chain) icon — not a "+" — so it
    // reads as "associate an existing custom object", not "create something"
    // (Margot 2026-10-21).
    //
    // Add-row (create-new): "Create <Template>" opens the create form for the
    // referencing template with the back-reference prefilled to this record and
    // returns here after saving (Stefan 2026-10-13); the list refetches when the
    // record screen remounts. Hidden while the link picker is open.
    const canCreate = canCreateIncomingReference(
        editable,
        recordId,
        node.referencing_object_template_id
    );
    const createNew = () => {
        if (!canCreate || !attribute) {
            return;
        }
        history.push(
            buildIncomingReferenceCreatePath(
                node.referencing_object_template_id as string,
                referencingTemplateName,
                attribute,
                recordId as string
            )
        );
    };
    const addBar =
        (canLinkExisting || canCreate) && recordId ? (
            <div className={styles.incomingRefAddBar}>
                {canCreate && !picking ? (
                    <Button
                        size="small"
                        variation="dark"
                        icon={IconCollection.plus}
                        label={incomingReferenceCreateLabel(referencingTemplateName)}
                        onClick={createNew}
                    />
                ) : null}
                {canLinkExisting ? (
                    <Button
                        size="small"
                        variation="dark"
                        // Link (chain) icon, not "+": linking associates an existing
                        // record, it does not create one. No icon on the Cancel
                        // affordance.
                        icon={picking ? undefined : IconCollection.link}
                        label={
                            picking ? "Cancel" : incomingReferenceLinkLabel(referencingTemplateName)
                        }
                        onClick={() => setPicking((prev) => !prev)}
                    />
                ) : null}
                {picking ? (
                    <div className={styles.incomingRefPicker}>
                        <AutocompleteFormComponent
                            key={pickerKey}
                            fieldProps={{
                                label: "",
                                // List only candidates whose back-reference is
                                // currently unset, so linking never silently
                                // re-parents (steals) a record already linked to
                                // another parent (Stefan 2026-10-13). canLinkExisting
                                // already guarantees the back-reference is nullable +
                                // the attribute is configured by this point.
                                options: buildReferenceValueOptions(
                                    "CUSTOM_OBJECT",
                                    node.referencing_object_template_id,
                                    node.incoming_reference_attribute
                                ),
                                customKey: `incomingRefLink-${
                                    node.referencing_object_template_id ?? "any"
                                }`,
                                fieldWidth: 320,
                            }}
                            // No placeholder "hint": the button already names
                            // what is being linked, and a sentence-length
                            // placeholder wrapped and overlapped the open menu in
                            // the narrow picker (Margot 2026-10-21). The field
                            // falls back to the short default affordance.
                            fieldAttr={{}}
                            input={
                                {
                                    name: "incomingRefLink",
                                    value: null,
                                    onChange: (val: unknown) => linkExisting(val),
                                    onBlur: () => undefined,
                                    onFocus: () => undefined,
                                } as unknown as FieldInputProps<unknown>
                            }
                            meta={{} as FieldMetaState<unknown>}
                        />
                    </div>
                ) : null}
            </div>
        ) : null;

    if (rows.length === 0) {
        return (
            <div className={styles.usedByFormContainer}>
                {addBar}
                <div className={styles.referencesEmpty}>No records reference this one yet.</div>
            </div>
        );
    }

    const tableSettingsKey = `incomingReference-${node.referencing_object_template_id ?? "any"}-${
        node.incoming_reference_attribute ?? "any"
    }`;

    return (
        <div className={styles.usedByFormContainer}>
            <div className={styles.referenceGroup}>
                {addBar}
                <RecordTable
                    columns={columns}
                    data={displayRows}
                    totalItemCount={rows.length}
                    isLoading={false}
                    recordTypes="all"
                    initialQuery={getInitialQueryWithTableSettings(tableSettingsKey, initialQuery)}
                    tableSettingsKey={tableSettingsKey}
                    options={{
                        ...tableOptions,
                        pagination: rows.length > initialQuery.limit,
                    }}
                    refresh={refetch}
                />
                {truncated ? (
                    <div className={styles.referenceGroupTruncated}>
                        Showing a sample — more references exist.
                    </div>
                ) : null}
            </div>
        </div>
    );
};
